import { useCallback, useEffect, useMemo, useRef, useState, type KeyboardEvent } from 'react'
import { Plus, Send, ChevronLeft, Users, Trash2, X, Pencil, UserPlus, LogOut, Check, Paperclip, Download, FileText, Loader2 } from 'lucide-react'
import type { ConversationDto, MessageDto, MemberDto, MessageAttachment } from '@familynest/shared'
import { api, ApiError } from '../../lib/api'
import { useAuth } from '../../store/auth'
import { useLive } from '../../lib/ws'
import { formatTime, formatRelative } from '../../lib/date'
import { Avatar, Button, ErrorBanner, useBackdropClose } from '../ui'
import { useDialogs } from '../ui/dialogs'
import { personTextColor } from '../../theme/persons'

function convName(c: ConversationDto, members: Map<string, MemberDto>, meId: string): string {
  if (c.name) return c.name
  const others = c.memberIds.filter((id) => id !== meId).map((id) => members.get(id)?.name ?? '?')
  return others.join(', ') || 'Unterhaltung'
}

export function ChatScreen({ members, onRead }: { members: MemberDto[]; onRead?: () => void }): React.JSX.Element {
  const { member: me } = useAuth()
  const memberMap = useMemo(() => new Map(members.map((m) => [m.id, m])), [members])
  const [convs, setConvs] = useState<ConversationDto[]>([])
  const [selectedId, setSelectedId] = useState<string | null>(null)
  const [newOpen, setNewOpen] = useState(false)

  const loadConvs = useCallback(async () => setConvs(await api.listConversations()), [])
  // Nach Chat-Aktivität (Lesen/Senden) BEIDE Unread-Badges aktualisieren: die
  // Konversationsliste hier + den Nav-Badge in der AppShell (ADR-074). Deterministisch,
  // nicht nur über den WS-Broadcast (der race-anfällig ist).
  const refreshUnread = useCallback(() => {
    void loadConvs()
    onRead?.()
  }, [loadConvs, onRead])
  useEffect(() => {
    void loadConvs()
  }, [loadConvs])
  useLive('chat', loadConvs)

  const selected = convs.find((c) => c.id === selectedId) ?? null

  return (
    <div className="grid h-full grid-cols-1 md:grid-cols-[20rem_1fr]">
      {/* Konversationsliste */}
      <aside className={`flex-col border-r ${selected ? 'hidden md:flex' : 'flex'}`} style={{ borderColor: 'var(--color-line)', backgroundColor: 'var(--color-card)' }}>
        <div className="flex items-center justify-between px-4 py-3">
          <h1 className="text-lg font-semibold" style={{ color: 'var(--color-ink)' }}>Nachrichten</h1>
          <Button onClick={() => setNewOpen(true)}><Plus className="size-4" /> Neu</Button>
        </div>
        <div className="flex-1 overflow-auto px-2 pb-2">
          {convs.map((c) => {
            const active = c.id === selectedId
            const isGroup = c.type === 'group'
            const otherId = c.memberIds.find((id) => id !== me?.id)
            const other = otherId ? memberMap.get(otherId) : null
            return (
              <button key={c.id} onClick={() => setSelectedId(c.id)} className="flex w-full items-center gap-3 rounded-lg px-3 py-2 text-left hover:bg-black/5" style={active ? { backgroundColor: 'var(--color-accent)' + '18' } : undefined}>
                {isGroup ? (
                  <span className="flex size-9 items-center justify-center rounded-full" style={{ backgroundColor: 'var(--color-accent)' }}><Users className="size-4 text-white" /></span>
                ) : other ? (
                  <Avatar name={other.name} color={other.color} size={36} src={other.avatarUrl} />
                ) : (
                  <span className="size-9 rounded-full" style={{ backgroundColor: 'var(--color-line)' }} />
                )}
                <span className="min-w-0 flex-1">
                  <span className="flex items-center gap-2">
                    <span className="truncate font-medium" style={{ color: 'var(--color-ink)' }}>{convName(c, memberMap, me?.id ?? '')}</span>
                    {c.lastMessage && <span className="ml-auto shrink-0 text-xs" style={{ color: 'var(--color-muted)' }}>{formatRelative(c.lastMessage.createdAt)}</span>}
                  </span>
                  <span className="flex items-center gap-2">
                    <span className="truncate text-sm" style={{ color: 'var(--color-muted)' }}>{c.lastMessage?.body ?? (c.lastMessage ? '(gelöscht)' : 'Noch keine Nachrichten')}</span>
                    {c.unreadCount > 0 && <span className="ml-auto shrink-0 rounded-full px-1.5 text-xs font-semibold text-white" style={{ backgroundColor: 'var(--color-accent)' }}>{c.unreadCount}</span>}
                  </span>
                </span>
              </button>
            )
          })}
        </div>
      </aside>

      {/* Chat-Verlauf */}
      <section className={`min-h-0 ${selected ? 'flex' : 'hidden md:flex'} flex-col`}>
        {selected && me ? (
          <ChatView key={selected.id} conv={selected} members={memberMap} meId={me.id} onBack={() => setSelectedId(null)} onActivity={refreshUnread} />
        ) : (
          <div className="flex flex-1 items-center justify-center p-8 text-center text-sm" style={{ color: 'var(--color-muted)' }}>Wähle eine Unterhaltung — oder starte eine neue.</div>
        )}
      </section>

      {newOpen && me && (
        <NewConversationDialog members={members.filter((m) => m.role !== 'guest' && m.id !== me.id)} onClose={() => setNewOpen(false)} onCreated={(id) => { setNewOpen(false); void loadConvs(); setSelectedId(id) }} />
      )}
    </div>
  )
}

function ChatView({ conv, members, meId, onBack, onActivity }: { conv: ConversationDto; members: Map<string, MemberDto>; meId: string; onBack: () => void; onActivity: () => void }): React.JSX.Element {
  const dialogs = useDialogs()
  const [messages, setMessages] = useState<MessageDto[]>([])
  const [draft, setDraft] = useState('')
  const [infoOpen, setInfoOpen] = useState(false)
  const [uploading, setUploading] = useState(false)
  const [uploadError, setUploadError] = useState<string | null>(null)
  const [loadError, setLoadError] = useState(false)
  const [sendError, setSendError] = useState<string | null>(null)
  const fileRef = useRef<HTMLInputElement>(null)
  const endRef = useRef<HTMLDivElement>(null)
  const listRef = useRef<HTMLDivElement>(null)

  const load = useCallback(async () => {
    try {
      const server = await api.listMessages(conv.id)
      // In-flight-Platzhalter (temp-…) NICHT wegwerfen — sonst überschreibt ein durch
      // das eigene Senden ausgelöstes Live-Update die optimistische Nachricht, bevor der
      // Server sie zurückliefert (ADR-071). Sie bleibt bis zur Bestätigung sichtbar.
      setMessages((prev) => {
        const temps = prev.filter((m) => m.id.startsWith('temp-'))
        return temps.length > 0 ? [...server, ...temps] : server
      })
      setLoadError(false)
      onActivity()
    } catch {
      setLoadError(true)
    }
  }, [conv.id, onActivity])
  useEffect(() => {
    void load()
  }, [load])
  useLive('chat', load)
  // Als gelesen markieren beim Öffnen + bei neuer (neuester) Nachricht — bewusst
  // NICHT in load(): markRead löst serverseitig einen chat-Broadcast aus, der load()
  // erneut triggert → sonst Endlosschleife markRead→broadcast→load→markRead (ADR-073).
  // Über die neueste Nachricht-ID gekoppelt: sobald „gelesen" aufgeholt ist, ändert
  // sie sich nicht mehr → der Effekt terminiert.
  const newestId = messages.length > 0 ? messages[messages.length - 1]!.id : null
  useEffect(() => {
    // Optimistische, noch nicht bestätigte eigene Nachricht (temp-…) → kein markRead
    // (spart einen redundanten POST + Preflight; der Sender gilt ohnehin als „gelesen").
    if (newestId?.startsWith('temp-')) return
    // Nach dem Persistieren die Unread-Badges gezielt neu laden (ADR-074) — sonst
    // bleibt der Zähler stehen, wenn der WS-Broadcast das Neuladen nicht rechtzeitig
    // (Race) oder gar nicht (Zustellung) auslöst.
    void api
      .markRead(conv.id)
      .then(() => onActivity())
      .catch(() => {})
  }, [conv.id, newestId, onActivity])
  const scrollToBottom = useCallback((): void => {
    // Nur den Nachrichten-Container nach unten scrollen (NICHT scrollIntoView, das auf
    // iOS auch das Fenster verschiebt) — ADR-065.
    if (listRef.current) listRef.current.scrollTop = listRef.current.scrollHeight
  }, [])
  useEffect(() => {
    scrollToBottom()
  }, [messages, scrollToBottom])
  useEffect(() => {
    // Öffnet die iOS-Tastatur, schrumpft die Liste → neueste Nachricht wieder sichtbar
    // machen (ADR-069). Die Tastaturhöhe steht teils erst einen Tick später fest.
    const onResize = (): void => {
      scrollToBottom()
      window.setTimeout(scrollToBottom, 250)
    }
    window.visualViewport?.addEventListener('resize', onResize)
    return () => window.visualViewport?.removeEventListener('resize', onResize)
  }, [scrollToBottom])

  async function send(): Promise<void> {
    const body = draft.trim()
    if (!body) return
    setDraft('')
    setSendError(null)
    // Optimistisch: Nachricht sofort anzeigen (temp-…), danach mit der Server-Antwort
    // abgleichen. Kein volles Neuladen → kein spürbarer Verzug (ADR-071).
    const tempId = `temp-${Date.now()}`
    const optimistic: MessageDto = { id: tempId, conversationId: conv.id, senderId: meId, body, attachment: null, createdAt: new Date().toISOString(), editedAt: null, deletedAt: null }
    setMessages((prev) => [...prev, optimistic])
    try {
      const real = await api.sendMessage(conv.id, body)
      setMessages((prev) => {
        // Platzhalter raus, echte Nachricht garantiert drin (egal ob ein WS-Load sie
        // zwischenzeitlich schon eingefügt oder den Platzhalter entfernt hat).
        const withoutTemp = prev.filter((m) => m.id !== tempId)
        return withoutTemp.some((m) => m.id === real.id) ? withoutTemp : [...withoutTemp, real]
      })
      onActivity()
    } catch {
      // Fehlgeschlagen: Platzhalter raus, Text zum erneuten Senden zurückgeben.
      setMessages((prev) => prev.filter((m) => m.id !== tempId))
      setDraft(body)
      setSendError('Nachricht konnte nicht gesendet werden. Bitte erneut versuchen.')
    }
  }
  async function onKey(e: KeyboardEvent<HTMLInputElement>): Promise<void> {
    if (e.key === 'Enter') await send()
  }
  async function remove(id: string): Promise<void> {
    if (!(await dialogs.confirm({ title: 'Nachricht löschen', message: 'Diese Nachricht löschen?', confirmLabel: 'Löschen', danger: true }))) return
    await api.deleteMessage(id)
    await load()
  }
  async function onPickFile(e: React.ChangeEvent<HTMLInputElement>): Promise<void> {
    const file = e.target.files?.[0]
    e.target.value = ''
    if (!file) return
    setUploading(true)
    setUploadError(null)
    try {
      await api.uploadAttachment(conv.id, file)
      await load()
    } catch (err) {
      setUploadError(err instanceof ApiError && err.status === 413 ? 'Datei zu groß (max. 25 MB).' : 'Upload fehlgeschlagen.')
    } finally {
      setUploading(false)
    }
  }

  const title = conv.name ?? conv.memberIds.filter((id) => id !== meId).map((id) => members.get(id)?.name ?? '?').join(', ')

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <div className="flex items-center gap-2 border-b px-3 py-3" style={{ borderColor: 'var(--color-line)' }}>
        <button onClick={onBack} className="rounded-lg p-1 hover:bg-black/5 md:hidden" aria-label="Zurück"><ChevronLeft className="size-5" style={{ color: 'var(--color-ink)' }} /></button>
        {conv.type === 'group' ? (
          <button onClick={() => setInfoOpen(true)} className="flex min-w-0 items-center gap-2 rounded-lg px-1 py-0.5 text-left hover:bg-black/5" title="Gruppeninfo">
            <span className="flex size-7 shrink-0 items-center justify-center rounded-full" style={{ backgroundColor: 'var(--color-accent)' }}><Users className="size-4 text-white" /></span>
            <span className="min-w-0">
              <span className="block truncate font-semibold" style={{ color: 'var(--color-ink)' }}>{title}</span>
              <span className="block text-xs" style={{ color: 'var(--color-muted)' }}>{conv.memberIds.length} Mitglieder · tippen zum Verwalten</span>
            </span>
          </button>
        ) : (
          <h2 className="font-semibold" style={{ color: 'var(--color-ink)' }}>{title}</h2>
        )}
      </div>

      <div ref={listRef} className="flex flex-1 flex-col gap-2 overflow-auto p-3">
        {loadError && <ErrorBanner message="Nachrichten konnten nicht geladen werden." onRetry={() => void load()} />}
        {messages.length === 0 && !loadError && <p className="py-10 text-center text-sm" style={{ color: 'var(--color-muted)' }}>Sag Hallo 👋</p>}
        {messages.map((msg) => {
          const mine = msg.senderId === meId
          const sender = msg.senderId ? members.get(msg.senderId) : null
          const deleted = msg.deletedAt !== null
          const pending = msg.id.startsWith('temp-')
          return (
            <div key={msg.id} className={`group flex flex-col ${mine ? 'items-end' : 'items-start'} ${pending ? 'opacity-60' : ''}`}>
              <div className={`flex max-w-[80%] items-end gap-2 ${mine ? 'flex-row-reverse' : ''}`}>
                {!mine && sender && <Avatar name={sender.name} color={sender.color} size={26} src={sender.avatarUrl} />}
                <div className="rounded-2xl px-3 py-2 text-sm" style={mine ? { backgroundColor: 'var(--color-accent)', color: '#fff' } : { backgroundColor: 'var(--color-card)', border: '1px solid var(--color-line)', color: 'var(--color-ink)' }}>
                  {!mine && conv.type === 'group' && sender && <div className="mb-0.5 text-xs font-medium" style={{ color: personTextColor(sender.color) }}>{sender.name}</div>}
                  {deleted ? (
                    <div className="italic opacity-70">Nachricht gelöscht</div>
                  ) : (
                    <>
                      {msg.attachment && <AttachmentView messageId={msg.id} attachment={msg.attachment} mine={mine} />}
                      {msg.body && <div className="whitespace-pre-wrap">{msg.body}</div>}
                    </>
                  )}
                </div>
                {mine && !deleted && !pending && (
                  <button onClick={() => remove(msg.id)} className="rounded p-1 opacity-0 hover:bg-black/5 group-hover:opacity-100" aria-label="Löschen"><Trash2 className="size-3.5" style={{ color: 'var(--color-muted)' }} /></button>
                )}
              </div>
              <span className="px-1 text-[10px]" style={{ color: 'var(--color-muted)' }}>{pending ? 'senden…' : formatTime(msg.createdAt)}</span>
            </div>
          )
        })}
        <div ref={endRef} />
      </div>

      {uploadError && <p className="px-3 pb-1 text-xs" style={{ color: 'var(--color-danger)' }}>{uploadError}</p>}
      {sendError && <p className="px-3 pb-1 text-xs" style={{ color: 'var(--color-danger)' }}>{sendError}</p>}
      <div className="flex items-center gap-2 border-t p-3" style={{ borderColor: 'var(--color-line)' }}>
        <input ref={fileRef} type="file" className="hidden" onChange={onPickFile} />
        <button onClick={() => fileRef.current?.click()} disabled={uploading} className="rounded-full p-2.5 hover:bg-black/5 disabled:opacity-50" aria-label="Datei anhängen" title="Datei anhängen">
          {uploading ? <Loader2 className="size-4 animate-spin" style={{ color: 'var(--color-muted)' }} /> : <Paperclip className="size-4" style={{ color: 'var(--color-muted)' }} />}
        </button>
        <input value={draft} onChange={(e) => setDraft(e.target.value)} onKeyDown={onKey} onFocus={() => window.setTimeout(scrollToBottom, 300)} placeholder="Nachricht…" className="flex-1 rounded-full border px-4 py-2 outline-none focus:border-[var(--color-accent)]" style={{ backgroundColor: 'var(--color-surface)', borderColor: 'var(--color-line)', color: 'var(--color-ink)' }} />
        <button onClick={send} disabled={!draft.trim()} className="rounded-full p-2.5 disabled:opacity-50" style={{ backgroundColor: 'var(--color-accent)' }} aria-label="Senden"><Send className="size-4 text-white" /></button>
      </div>

      {infoOpen && (
        <GroupInfoDialog
          conv={conv}
          members={members}
          meId={meId}
          onClose={() => setInfoOpen(false)}
          onChanged={() => { void load(); onActivity() }}
          onLeft={() => { setInfoOpen(false); onBack(); onActivity() }}
        />
      )}
    </div>
  )
}

/** Gruppeninfo + Verwaltung: umbenennen, Mitglieder hinzufügen/entfernen, verlassen.
    Die System-Gruppe „Familie" (createdBy === null) ist nur lesbar. */
function GroupInfoDialog({ conv, members, meId, onClose, onChanged, onLeft }: { conv: ConversationDto; members: Map<string, MemberDto>; meId: string; onClose: () => void; onChanged: () => void; onLeft: () => void }): React.JSX.Element {
  const dialogs = useDialogs()
  const manageable = conv.createdBy !== null
  const [name, setName] = useState(conv.name ?? '')
  const [busy, setBusy] = useState(false)
  const [adding, setAdding] = useState(false)

  const inGroup = new Set(conv.memberIds)
  const candidates = Array.from(members.values()).filter((m) => m.role !== 'guest' && !inGroup.has(m.id))

  async function rename(): Promise<void> {
    const n = name.trim()
    if (!n || n === conv.name) return
    setBusy(true)
    try { await api.renameConversation(conv.id, n); onChanged() } finally { setBusy(false) }
  }
  async function addMember(id: string): Promise<void> {
    setBusy(true)
    try { await api.addConversationMembers(conv.id, [id]); onChanged() } finally { setBusy(false) }
  }
  async function removeMember(id: string): Promise<void> {
    setBusy(true)
    try { await api.removeConversationMember(conv.id, id); onChanged() } finally { setBusy(false) }
  }
  async function leave(): Promise<void> {
    if (!(await dialogs.confirm({ title: 'Gruppe verlassen', message: 'Gruppe wirklich verlassen?', confirmLabel: 'Verlassen', danger: true }))) return
    setBusy(true)
    try { await api.removeConversationMember(conv.id, meId); onLeft() } catch { setBusy(false) }
  }

  const backdrop = useBackdropClose(onClose)
  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center bg-black/40 p-0 sm:items-center sm:p-4" {...backdrop}>
      <div className="max-h-[90vh] w-full max-w-sm overflow-auto rounded-t-2xl border p-5 sm:rounded-2xl" style={{ backgroundColor: 'var(--color-card)', borderColor: 'var(--color-line)', paddingBottom: 'calc(1.25rem + env(safe-area-inset-bottom))' }} onClick={(e) => e.stopPropagation()}>
        <div className="mb-4 flex items-center justify-between">
          <h2 className="text-lg font-semibold" style={{ color: 'var(--color-ink)' }}>Gruppeninfo</h2>
          <button onClick={onClose} aria-label="Schließen" className="rounded-lg p-1 hover:bg-black/5"><X className="size-5" style={{ color: 'var(--color-muted)' }} /></button>
        </div>

        {manageable ? (
          <label className="mb-4 flex items-center gap-2">
            <input value={name} onChange={(e) => setName(e.target.value)} className="flex-1 rounded-lg border px-3 py-2 outline-none focus:border-[var(--color-accent)]" style={{ backgroundColor: 'var(--color-surface)', borderColor: 'var(--color-line)', color: 'var(--color-ink)' }} placeholder="Gruppenname" />
            <button onClick={rename} disabled={busy || !name.trim() || name.trim() === conv.name} className="rounded-lg p-2 disabled:opacity-40" style={{ backgroundColor: 'var(--color-accent)' }} aria-label="Umbenennen"><Pencil className="size-4 text-white" /></button>
          </label>
        ) : (
          <p className="mb-3 text-sm font-medium" style={{ color: 'var(--color-ink)' }}>{conv.name}</p>
        )}

        <p className="mb-1 text-xs font-semibold uppercase tracking-wide" style={{ color: 'var(--color-muted)' }}>Mitglieder ({conv.memberIds.length})</p>
        <div className="flex flex-col">
          {conv.memberIds.map((id) => {
            const m = members.get(id)
            return (
              <div key={id} className="flex items-center gap-3 py-1.5">
                {m ? <Avatar name={m.name} color={m.color} size={30} src={m.avatarUrl} /> : <span className="size-7 rounded-full" style={{ backgroundColor: 'var(--color-line)' }} />}
                <span className="flex-1 text-sm" style={{ color: 'var(--color-ink)' }}>{m?.name ?? '?'}{id === meId ? ' (du)' : ''}</span>
                {manageable && id !== meId && (
                  <button onClick={() => removeMember(id)} disabled={busy} className="rounded p-1 hover:bg-black/5" aria-label="Entfernen"><Trash2 className="size-4" style={{ color: 'var(--color-muted)' }} /></button>
                )}
              </div>
            )
          })}
        </div>

        {manageable && (
          <>
            {adding ? (
              <div className="mt-2 rounded-lg border p-2" style={{ borderColor: 'var(--color-line)' }}>
                {candidates.length === 0 ? (
                  <p className="px-1 py-2 text-sm" style={{ color: 'var(--color-muted)' }}>Alle sind schon dabei.</p>
                ) : (
                  candidates.map((m) => (
                    <button key={m.id} onClick={() => addMember(m.id)} disabled={busy} className="flex w-full items-center gap-3 rounded-lg px-2 py-1.5 text-left hover:bg-black/5">
                      <Avatar name={m.name} color={m.color} size={28} src={m.avatarUrl} />
                      <span className="flex-1 text-sm" style={{ color: 'var(--color-ink)' }}>{m.name}</span>
                      <Check className="size-4" style={{ color: 'var(--color-accent)' }} />
                    </button>
                  ))
                )}
                <button onClick={() => setAdding(false)} className="mt-1 w-full rounded-lg px-2 py-1.5 text-sm" style={{ color: 'var(--color-muted)' }}>Fertig</button>
              </div>
            ) : (
              <button onClick={() => setAdding(true)} className="mt-2 flex w-full items-center gap-2 rounded-lg px-2 py-2 text-sm hover:bg-black/5" style={{ color: 'var(--color-accent)' }}>
                <UserPlus className="size-4" /> Mitglied hinzufügen
              </button>
            )}
            <button onClick={leave} disabled={busy} className="mt-3 flex w-full items-center justify-center gap-2 rounded-lg px-2 py-2 text-sm" style={{ color: 'var(--color-danger)' }}>
              <LogOut className="size-4" /> Gruppe verlassen
            </button>
          </>
        )}
      </div>
    </div>
  )
}

function formatBytes(n: number): string {
  if (n < 1024) return `${n} B`
  if (n < 1024 * 1024) return `${Math.round(n / 1024)} KB`
  return `${(n / 1024 / 1024).toFixed(1)} MB`
}

/** Zeigt einen Nachrichten-Anhang. Bilder werden inline geladen (Blob mit Auth),
    andere Dateien als Karte mit Download-Button. Alles über die geschützte Route. */
function AttachmentView({ messageId, attachment, mine }: { messageId: string; attachment: MessageAttachment; mine: boolean }): React.JSX.Element {
  const isImage = attachment.mime.startsWith('image/')
  const [url, setUrl] = useState<string | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(false)

  useEffect(() => {
    if (!isImage) return
    let objUrl: string | null = null
    let alive = true
    setLoading(true)
    api.attachmentObjectUrl(messageId)
      .then((u) => { if (alive) { objUrl = u; setUrl(u) } else URL.revokeObjectURL(u) })
      .catch(() => { if (alive) setError(true) })
      .finally(() => { if (alive) setLoading(false) })
    return () => { alive = false; if (objUrl) URL.revokeObjectURL(objUrl) }
  }, [messageId, isImage])

  async function download(): Promise<void> {
    setLoading(true)
    try {
      const u = await api.attachmentObjectUrl(messageId)
      const a = document.createElement('a')
      a.href = u
      a.download = attachment.name
      document.body.appendChild(a)
      a.click()
      a.remove()
      setTimeout(() => URL.revokeObjectURL(u), 10000)
    } catch {
      setError(true)
    } finally {
      setLoading(false)
    }
  }

  if (isImage) {
    return (
      <div className="my-1">
        {url ? (
          <a href={url} target="_blank" rel="noreferrer"><img src={url} alt={attachment.name} className="max-h-64 max-w-full rounded-lg object-contain" /></a>
        ) : (
          <div className="flex h-32 w-40 items-center justify-center rounded-lg" style={{ backgroundColor: 'var(--color-surface)' }}>
            {error ? <span className="text-xs" style={{ color: 'var(--color-danger)' }}>Fehler</span> : <Loader2 className="size-5 animate-spin" style={{ color: 'var(--color-muted)' }} />}
          </div>
        )}
      </div>
    )
  }
  return (
    <button onClick={download} disabled={loading} className="my-1 flex items-center gap-2 rounded-lg px-2 py-1.5" style={{ backgroundColor: mine ? 'rgba(255,255,255,0.15)' : 'var(--color-surface)' }}>
      <FileText className="size-5 shrink-0" style={{ color: mine ? '#fff' : 'var(--color-accent)' }} />
      <span className="min-w-0 text-left">
        <span className="block max-w-[10rem] truncate text-sm" style={{ color: mine ? '#fff' : 'var(--color-ink)' }}>{attachment.name}</span>
        <span className="block text-xs" style={{ color: mine ? 'rgba(255,255,255,0.85)' : 'var(--color-muted)' }}>{error ? 'Fehler' : formatBytes(attachment.size)}</span>
      </span>
      {loading ? <Loader2 className="size-4 shrink-0 animate-spin" style={{ color: mine ? '#fff' : 'var(--color-muted)' }} /> : <Download className="size-4 shrink-0" style={{ color: mine ? '#fff' : 'var(--color-muted)' }} />}
    </button>
  )
}

function NewConversationDialog({ members, onClose, onCreated }: { members: MemberDto[]; onClose: () => void; onCreated: (id: string) => void }): React.JSX.Element {
  const [selected, setSelected] = useState<Set<string>>(new Set())
  const [name, setName] = useState('')
  const [busy, setBusy] = useState(false)
  const isGroup = selected.size > 1

  function toggle(id: string): void {
    setSelected((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  async function create(): Promise<void> {
    setBusy(true)
    try {
      const ids = Array.from(selected)
      const conv = await api.createConversation({ type: isGroup ? 'group' : 'direct', memberIds: ids, ...(isGroup && name.trim() ? { name: name.trim() } : {}) })
      onCreated(conv.id)
    } finally {
      setBusy(false)
    }
  }

  const backdrop = useBackdropClose(onClose)
  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center bg-black/40 p-0 sm:items-center sm:p-4" {...backdrop}>
      <div className="max-h-[90vh] w-full max-w-sm overflow-auto rounded-t-2xl border p-5 sm:rounded-2xl" style={{ backgroundColor: 'var(--color-card)', borderColor: 'var(--color-line)' }} onClick={(e) => e.stopPropagation()}>
        <div className="mb-4 flex items-center justify-between">
          <h2 className="text-lg font-semibold" style={{ color: 'var(--color-ink)' }}>Neue Unterhaltung</h2>
          <button onClick={onClose} aria-label="Schließen" className="rounded-lg p-1 hover:bg-black/5"><X className="size-5" style={{ color: 'var(--color-muted)' }} /></button>
        </div>
        <p className="mb-2 text-sm" style={{ color: 'var(--color-muted)' }}>Personen wählen (mehrere = Gruppe):</p>
        <div className="flex flex-col gap-1">
          {members.map((m) => {
            const on = selected.has(m.id)
            return (
              <button key={m.id} onClick={() => toggle(m.id)} className="flex items-center gap-3 rounded-lg px-2 py-1.5 hover:bg-black/5" style={on ? { backgroundColor: 'var(--color-accent)' + '18' } : undefined}>
                <Avatar name={m.name} color={m.color} size={30} src={m.avatarUrl} />
                <span className="flex-1 text-left" style={{ color: 'var(--color-ink)' }}>{m.name}</span>
                <span className="flex size-5 items-center justify-center rounded-full text-xs text-white" style={{ backgroundColor: on ? 'var(--color-accent)' : 'transparent', border: on ? 'none' : '2px solid var(--color-line)' }}>{on ? '✓' : ''}</span>
              </button>
            )
          })}
        </div>
        {isGroup && (
          <input value={name} onChange={(e) => setName(e.target.value)} placeholder="Gruppenname (optional)" className="mt-3 w-full rounded-lg border px-3 py-2 outline-none focus:border-[var(--color-accent)]" style={{ backgroundColor: 'var(--color-surface)', borderColor: 'var(--color-line)', color: 'var(--color-ink)' }} />
        )}
        <div className="mt-4 flex justify-end gap-2">
          <Button variant="ghost" onClick={onClose} disabled={busy}>Abbrechen</Button>
          <Button onClick={create} disabled={busy || selected.size === 0}>{isGroup ? 'Gruppe erstellen' : 'Chat starten'}</Button>
        </div>
      </div>
    </div>
  )
}
